import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS = {
  wired: { label: 'WIRED', bg: 'rgba(29,158,117,0.12)', fg: '#1D9E75' },
  partial: { label: 'PARTIAL', bg: 'rgba(232,160,32,0.14)', fg: '#B07407' },
  described: { label: 'DESCRIBED', bg: 'rgba(136,150,168,0.14)', fg: '#5C6B7A' },
};

export default function ModeMatrixPage() {
  const api = useApi();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/operating-modes').then(r => setData(r.data)).catch(() => setData({}));
  }, [api]);

  if (!data) return <Skeleton className="h-96 rounded-sm" data-testid="matrix-loading" />;

  const modes = Object.values(data?.categories || {}).flatMap(cat => (cat.modes || []).map(m => ({ ...m, catName: cat.name })));

  return (
    <div className="space-y-3" data-testid="mode-matrix-page">
      <p className="text-sm max-w-3xl" style={{ color: '#5C6B7A' }}>
        All {modes.length} modes on one page — what each one declares for scoring, binding, claims
        and automation, beside whether the platform actually reads it today.
      </p>
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-x-auto">
        <table className="w-full text-[10px]" data-testid="mode-matrix-table">
          <thead>
            <tr style={{ background: '#0B1E3D' }}>
              <th className="text-left p-2 text-white font-semibold sticky left-0" style={{ background: '#0B1E3D' }}>Mode</th>
              <th className="text-center p-2 text-white font-semibold">State</th>
              <th className="text-center p-2 text-white font-semibold">Scoring</th>
              <th className="text-center p-2 text-white font-semibold">Binding</th>
              <th className="text-center p-2 text-white font-semibold">Claims</th>
              <th className="text-center p-2 text-white font-semibold">Automation</th>
              <th className="text-center p-2 text-white font-semibold">Human override</th>
              <th className="text-center p-2 text-white font-semibold">Visibility</th>
            </tr>
          </thead>
          <tbody>
            {modes.map((mode, idx) => {
              const st = STATUS[mode.enforcement?.status] || STATUS.described;
              const zebra = idx % 2 === 0;
              return (
                <tr key={mode.id} style={zebra ? { background: '#F5F7FA' } : {}} data-testid={`matrix-row-${mode.id}`}>
                  <td className="p-2 font-semibold sticky left-0" style={{ color: '#0B1E3D', background: zebra ? '#F5F7FA' : '#FFF' }}>
                    {mode.name}
                    <span className="block text-[9px] font-normal font-mono" style={{ color: '#8896A8' }}>{mode.id}</span>
                  </td>
                  <td className="p-2 text-center">
                    <span className="px-1.5 py-0.5 rounded-sm font-bold" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                  </td>
                  <td className="p-2 text-center"><ModeCell value={mode.scoring} /></td>
                  <td className="p-2 text-center"><ModeCell value={mode.binding} /></td>
                  <td className="p-2 text-center"><ModeCell value={mode.claims} /></td>
                  <td className="p-2 text-center"><ModeCell value={mode.automation_level} /></td>
                  <td className="p-2 text-center"><ModeCell value={mode.human_override} /></td>
                  <td className="p-2 text-center"><ModeCell value={mode.visibility} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function ModeCell({ value }) {
  if (!value || value === 'n/a') return <span style={{ color: '#CBD5E1' }}>—</span>;
  if (value === 'disabled') return <span className="px-1 py-0.5 rounded" style={{ background: 'rgba(226,75,74,0.1)', color: '#E24B4A' }}>off</span>;
  if (/active|full|instant|maximum|automatic/.test(value))
    return <span className="px-1 py-0.5 rounded" style={{ background: 'rgba(29,158,117,0.1)', color: '#1D9E75' }}>{value.slice(0, 22)}</span>;
  if (/manual|mandatory|dual/.test(value))
    return <span className="px-1 py-0.5 rounded" style={{ background: 'rgba(232,160,32,0.12)', color: '#B07407' }}>{value.slice(0, 22)}</span>;
  return <span className="px-1 py-0.5 rounded" style={{ background: 'rgba(136,150,168,0.08)', color: '#5C6B7A' }}>{value.slice(0, 22)}</span>;
}
